/**
 * Document-related enums for the unified document management system.
 * These enums match the backend domain enums exactly.
 */

/**
 * Types of documents that can be uploaded to the system.
 */
export enum DocumentType {
	// Banking documents
	BANK_ACCOUNT_CERTIFICATE = "bank_account_certificate",
	IBAN_CERTIFICATE = "iban_certificate",

	// Tax & Government certificates
	TAX_ID_CERTIFICATE = "tax_id_certificate",
	TAX_CERTIFICATE = "tax_certificate",
	CENSUS_CERTIFICATE = "census_certificate",
	ICAA_CERTIFICATE = "icaa_certificate",
	INAEM_CERTIFICATE = "inaem_certificate",

	// Corporate documents
	COMPANY_REGISTRATION = "company_registration",
	ARTICLES_OF_ASSOCIATION = "articles_of_association",
	POWER_OF_ATTORNEY = "power_of_attorney",

	// Identity documents
	NATIONAL_ID = "national_id",
	PASSPORT = "passport",
	RESIDENCE_PERMIT = "residence_permit",

	// Financial documents
	INCOME_STATEMENT = "income_statement",
	WEALTH_DECLARATION = "wealth_declaration",
	FINANCIAL_STATEMENT = "financial_statement",

	// Investment/Contract documents
	INVESTMENT_CONTRACT = "investment_contract",
	RESERVATION_CONTRACT = "reservation_contract",
	FINANCING_CONTRACT = "financing_contract",
	PAYMENT_RECEIPT = "payment_receipt",
	TAX_SUBMISSION_CERTIFICATE = "tax_submission_certificate",
	CONTRACT = "contract",
	SIGNATURE_METADATA = "signature_metadata",

	// Other
	PROOF_OF_ADDRESS = "proof_of_address",
	KYC_FORM = "kyc_form",
	OTHER = "other",
}

/**
 * Promoter-specific document types for the upload modal dropdown.
 */
export const PROMOTER_DOCUMENT_TYPES = [
	// Certificates
	DocumentType.TAX_ID_CERTIFICATE,
	DocumentType.TAX_CERTIFICATE,
	DocumentType.CENSUS_CERTIFICATE,
	DocumentType.ICAA_CERTIFICATE,
	DocumentType.INAEM_CERTIFICATE,
	// Banking
	DocumentType.BANK_ACCOUNT_CERTIFICATE,
	DocumentType.IBAN_CERTIFICATE,
	// Corporate
	DocumentType.COMPANY_REGISTRATION,
	DocumentType.ARTICLES_OF_ASSOCIATION,
	DocumentType.POWER_OF_ATTORNEY,
	// Other
	DocumentType.PROOF_OF_ADDRESS,
	DocumentType.KYC_FORM,
	DocumentType.OTHER,
] as const;

export type PromoterDocumentType = (typeof PROMOTER_DOCUMENT_TYPES)[number];

/**
 * Entity types that can own documents.
 */
export enum EntityType {
	PROMOTER = "promoter",
	PROMOTER_BANK_ACCOUNT = "promoter_bank_account",
	INVESTOR = "investor",
	INVESTMENT = "investment",
}

/**
 * Status of a document in the system.
 */
export enum DocumentStatus {
	PENDING = "pending",
	APPROVED = "approved",
	REJECTED = "rejected",
	SIGNED = "signed",
	VOIDED = "voided",
	EXPIRED = "expired",
	DECLINED = "declined",
	SENT = "sent",
	FAILED = "failed",
}

/**
 * Helper type for document type values
 */
export type DocumentTypeValue = `${DocumentType}`;

/**
 * Helper type for entity type values
 */
export type EntityTypeValue = `${EntityType}`;

/**
 * Helper type for document status values
 */
export type DocumentStatusValue = `${DocumentStatus}`;
